/**
 * Pure helpers for NPC facing — used by C3 (initial sprite frame), C6
 * (talk-to-front approach selection), and the existing trainer LOS scan.
 *
 * Two responsibilities:
 *   1. Project a runtime.json `movementType` to a cardinal direction (or
 *      null for wandering / unsupported types).
 *   2. Project a cardinal direction + sprite layout to a Phaser frame
 *      index + flipX bit.
 *
 * Pure functions, no Phaser deps — fully unit-testable.
 */
import type { SpriteLayout } from '../data/spriteManifest';

export type Direction = 'up' | 'down' | 'left' | 'right';

/** Project a runtime.json `MOVEMENT_TYPE_*` value to a cardinal facing.
 *  Returns null for movement types that don't have a single canonical
 *  facing (WANDER_AROUND, walk-in-circle, etc.). MOVEMENT_TYPE_FACE_LEFT_
 *  AND_RIGHT + FACE_DOWN_AND_UP collapse to one of the two endpoints —
 *  cartridge defaults to the first axis member (left, down). */
export function facingFromMovementType(mt: string | undefined): Direction | null {
  switch (mt) {
    case 'MOVEMENT_TYPE_FACE_DOWN':
    case 'MOVEMENT_TYPE_FACE_DOWN_AND_UP':
      return 'down';
    case 'MOVEMENT_TYPE_FACE_UP':
      return 'up';
    case 'MOVEMENT_TYPE_FACE_LEFT':
    case 'MOVEMENT_TYPE_FACE_LEFT_AND_RIGHT':
      return 'left';
    case 'MOVEMENT_TYPE_FACE_RIGHT':
      return 'right';
    default:
      return null;
  }
}

export interface FrameSpec {
  /** Index into the spritesheet (0-based). */
  frame: number;
  /** Whether to flip horizontally — the cartridge convention is one
   *  side-facing strip used for both left + right via flipX. */
  flipX: boolean;
}

/** Project a cardinal direction + sprite layout to a Phaser frame index.
 *  Layout conventions (verified against pokefirered
 *  `src/data/object_events/object_event_anims.h`):
 *    - `directional-9`: 9-frame 16×32. Standing frames at [down=0, up=1,
 *      side=2 (flipX for left)] — same as `directional-3`. Walk frames
 *      cluster after: down 3/4, up 5/6, side 7/8. Use `walkCycleFrame`
 *      for per-step walking animation; this function returns only the
 *      standing pose.
 *    - `directional-3`: 3-frame 16×32 static-facing. [down=0, up=1, side=2
 *      (flipX for left)]. No walk frames.
 *    - `static`: single-frame sprites (item balls, Pokémon overworld bobs,
 *      NPCs that face only one way). Always frame 0; flipX never set.
 *
 *  For `directional-9` and `directional-3`, an unknown facing falls back
 *  to the down-facing frame so NPCs without a movementType (e.g.
 *  cartridge type:"clone" entries) still render plausibly.
 */
export function frameForFacing(layout: SpriteLayout, facing: Direction | null): FrameSpec {
  if (layout === 'static') {
    return { frame: 0, flipX: false };
  }
  // directional-3 and directional-9 share the same standing-frame
  // convention (cartridge FaceSouth=0, FaceNorth=1, FaceWest=2). They
  // differ only in whether walk frames exist past index 2.
  //
  // Frame 2 is drawn FACING LEFT (sAnim_FaceWest in cartridge anims.h);
  // sAnim_GoEast is the same frames with `.hFlip = TRUE`. So flipX is
  // applied for RIGHT-facing, not left — same convention as the player
  // sprite at setPlayerSpriteFrame.
  switch (facing) {
    case 'up': return { frame: 1, flipX: false };
    case 'left': return { frame: 2, flipX: false };
    case 'right': return { frame: 2, flipX: true };
    case 'down':
    default: return { frame: 0, flipX: false };
  }
}

/** Pick the per-step frame for a 4-step walk cycle in the cartridge
 *  pattern `[standing, walkA, standing, walkB]`. Mirrors pokefirered's
 *  sAnim_GoSouth/North/West/East tables: each direction's standing frame
 *  alternates with two walk frames (left foot, right foot) for the
 *  illusion of stepping.
 *
 *  Cartridge directional-9 walk frame map:
 *    - down: walkA=3, walkB=4
 *    - up:   walkA=5, walkB=6
 *    - side: walkA=7, walkB=8 (flipX for left, handled by frameForFacing)
 *
 *  `directional-3` and `static` layouts have no walk frames; this returns
 *  the standing frame each step (correct facing, no leg animation). */
export function walkCycleFrame(
  layout: SpriteLayout,
  facing: Direction | null,
  cycleIndex: number,
): number {
  const standing = frameForFacing(layout, facing).frame;
  if (layout !== 'directional-9') return standing;
  let walkA: number;
  let walkB: number;
  switch (facing) {
    case 'up': walkA = 5; walkB = 6; break;
    case 'left':
    case 'right': walkA = 7; walkB = 8; break;
    case 'down':
    default: walkA = 3; walkB = 4; break;
  }
  const cycle = [standing, walkA, standing, walkB];
  return cycle[((cycleIndex % 4) + 4) % 4]!;
}

/** Project an NPC's tile + facing to the tile DIRECTLY IN FRONT of it.
 *  The cartridge approach-from-front convention: a player standing on
 *  the front tile looks back at the NPC to talk. Returns null if the
 *  NPC has no determined facing (caller falls back to closest-walkable). */
export function frontTileOf(
  npcTile: { x: number; y: number },
  facing: Direction | null,
): { x: number; y: number } | null {
  if (!facing) return null;
  switch (facing) {
    case 'up': return { x: npcTile.x, y: npcTile.y - 1 };
    case 'down': return { x: npcTile.x, y: npcTile.y + 1 };
    case 'left': return { x: npcTile.x - 1, y: npcTile.y };
    case 'right': return { x: npcTile.x + 1, y: npcTile.y };
  }
}
